import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Server, Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApiSettings = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const [config, setConfig] = useState({
    api_key: '',
    secret: '',
    slug: '',
    api_url: '',
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API}/admin/api-config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setConfig(data.data);
        }
      } catch (err) {
        setError('Failed to load configuration');
      }
      setLoading(false);
    };
    fetchConfig();
  }, [token]);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const res = await fetch(`${API}/admin/api-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('Configuration saved. Connection verified.');
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to save');
      }
    } catch (err) {
      setError('Connection error');
    }

    setSaving(false);
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin')}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-white">API Configuration</h1>
            <p className="text-xs text-zinc-500">Jisr HR API settings</p>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 text-zinc-500 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">API URL</label>
              <Input
                value={config.api_url}
                onChange={(e) => updateField('api_url', e.target.value)}
                placeholder="https://apis.jisr.net/api"
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                data-testid="config-api-url"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">Slug</label>
              <Input
                value={config.slug}
                onChange={(e) => updateField('slug', e.target.value)}
                placeholder="company-slug"
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                data-testid="config-slug"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">API Key</label>
              <div className="relative">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={config.api_key}
                  onChange={(e) => updateField('api_key', e.target.value)}
                  placeholder="API Key"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm pr-10"
                  data-testid="config-api-key"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">Secret</label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={config.secret}
                  onChange={(e) => updateField('secret', e.target.value)}
                  placeholder="API Secret"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm pr-10"
                  data-testid="config-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400 text-sm" data-testid="config-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 p-3 bg-emerald-900/20 border border-emerald-800/50 rounded-lg text-emerald-400 text-sm" data-testid="config-success">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            <Button
              onClick={handleSave}
              disabled={saving || !config.api_key || !config.secret || !config.slug || !config.api_url}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-config-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Testing & Saving...</>
              ) : (
                <><Server className="w-4 h-4 mr-2" /> Save & Test Connection</>
              )}
            </Button>

            <p className="text-[11px] text-zinc-600 text-center">
              Connection will be tested before saving. Dashboard cache will be cleared.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiSettings;
